import * as React from 'react';
import { isBrowser } from '@packages/common';
import { useTranslations } from '@packages/i18n';

import { breakpointSizes } from '../../theme';
import { Icon } from '../icons';
import { BackToTopButtonLabel, Oval, LiftedUpOval } from './BackToTopButtonStyleComponents';

const SCROLL_HANDLER_TIMEOUT = 500;
export interface BackToTopProps {
  topDistance?: number;
}

export const getCurrentYPosition = () =>
  isBrowser() ? document.documentElement.scrollTop || window.pageYOffset || document.body.scrollTop || 0 : 0;

const isMobileSafari = () => {
  if (!isBrowser()) {
    return false;
  }

  const userAgent = window.navigator.userAgent;
  const mdSize = parseInt(breakpointSizes.md.slice(0, breakpointSizes.md.length - 2), 10);
  if (userAgent.match(/(iPod|iPhone)/) && userAgent.match(/AppleWebKit/) && window.innerWidth < mdSize) {
    return true;
  }

  return false;
};

export const BackToTopButton: React.FC<BackToTopProps> = ({ topDistance = 200 }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [debounceState, setDebounceState] = React.useState<NodeJS.Timeout>();
  const [debounceScrollEnd, setDebounceScrollEnd] = React.useState<NodeJS.Timeout>();
  const [lastYposition, setLastYposition] = React.useState(getCurrentYPosition());
  const backToTop = useTranslations('backToTop');

  const updateVisibilityState = (visible: boolean) => {
    clearTimeout(debounceState);
    const debnceState = setTimeout(() => {
      setIsVisible(visible);
    }, SCROLL_HANDLER_TIMEOUT);

    setDebounceState(debnceState);
  };

  const handleScroll = () => {
    const scrollY = getCurrentYPosition();
    const isScrollingDown = scrollY > lastYposition;
    setLastYposition(scrollY);
    if (scrollY < topDistance) {
      return updateVisibilityState(false);
    }

    if (isScrollingDown) {
      // On scrolling down, the button must be invisible on scroll start, and visible on scroll end
      setIsVisible(false);
      clearTimeout(debounceScrollEnd);
      const dbnceScrollEnd = setTimeout(() => updateVisibilityState(true), 0);
      setDebounceScrollEnd(dbnceScrollEnd);
    } else {
      // On scrolling up, button must be always visible
      setIsVisible(true);
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();
    window.scroll(0, 0);
  };

  const OvalClass = isMobileSafari() ? LiftedUpOval : Oval;
  return (
    <OvalClass isVisible={isVisible} onClick={handleClick} data-testid="back-to-top">
      <Icon name="chevronUp" />
      <BackToTopButtonLabel>{backToTop}</BackToTopButtonLabel>
    </OvalClass>
  );
};
